import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BaseCodeSelectbox from '../common/form/selectbox/BaseCodeSelectbox';
import DetailTemplate from '../common/template/DetailTemplate';
import PartnerModalConatiner from '../../containers/common/form/modal/PartnerModalConatiner';
import FormLabel from '../common/form/FormLabel';
import * as partnerApi from '../../lib/api/partner';
import { subscribePartnerCreated } from '../../utils/partnerCreatedEvent';

const ProjectForm = ({ formik }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  // 2026-10-19 (ksm_frontend에서 포팅, 요구사항 5/02): 고객사 선택 시 담당자 목록을 조회해
  // 고객 담당자(이름/이메일/연락처)를 자동으로 채운다.
  const [contactList, setContactList] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const selectedCustomerUuid = formik.values.customerUuid || formik.values.vendorCmp || '';

  // 2026-10-22 B3(ksm_frontend ProjectForm.js에서 포팅): 거래처를 새 탭에서 등록하고 돌아오면
  // 자동으로 선택되도록 반환 토큰을 발급해 새 탭에 전달한다.
  const partnerReturnTokenRef = useRef(
    `project-form-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );

  const partnerAdd = () => {
    navigate(`/partnermng/add`);
  };

  const selectedPartner = useCallback((partner) => {
    const partnerUuid = partner?.partnerUuid || '';
    formik.setFieldValue('partnerInc', partner?.partnerNm || '');
    formik.setFieldValue('customerUuid', partnerUuid);
    formik.setFieldValue('vendorCmp', partnerUuid);

    // 담당자 정보 초기화 - 거래처가 바뀌면 이전 거래처의 담당자 값이 남아있으면 안 된다.
    formik.setFieldValue('chargeNm', '');
    formik.setFieldValue('chargeEml', '');
    formik.setFieldValue('chargeTel', '');
    formik.setFieldValue('vendorEmp', '');
    setSelectedContact(null);
    setContactList([]);
  }, [formik]);

  // 고객명을 직접 입력 중이면 이전에 선택했던 거래처 연결을 끊는다(선택 상태로 남아있으면
  // 잘못된 거래처에 기회가 묶인다).
  const handlePartnerIncChange = (event) => {
    formik.handleChange(event);

    if (selectedCustomerUuid) {
      formik.setFieldValue('customerUuid', '');
      formik.setFieldValue('vendorCmp', '');
      formik.setFieldValue('chargeNm', '');
      formik.setFieldValue('chargeEml', '');
      formik.setFieldValue('chargeTel', '');
      formik.setFieldValue('vendorEmp', '');
      setSelectedContact(null);
      setContactList([]);
    }
  };

  const normalizePartnerName = (value) => {
    return (value || '').replace(/\s+/g, '').toLowerCase();
  };

  // 고객명 입력 후 포커스를 벗어나면 이름이 일치하는 거래처를 찾아 자동 선택한다
  // (완전일치 우선, 없으면 검색 결과가 1건일 때만).
  const handlePartnerIncBlur = async (event) => {
    formik.handleBlur(event);
    const partnerName = event.target.value?.trim();

    if (!partnerName || selectedCustomerUuid) {
      return;
    }

    try {
      const res = await partnerApi.getPartners({ partnerNm: partnerName });
      const partners = res.data?.list || [];
      const normalizedName = normalizePartnerName(partnerName);
      const exactMatch = partners.find(
        (partner) => normalizePartnerName(partner.partnerNm) === normalizedName,
      );
      const matchedPartner = exactMatch || (partners.length === 1 ? partners[0] : null);

      if (matchedPartner?.partnerUuid) {
        selectedPartner(matchedPartner);
      }
    } catch (err) {
      console.error('고객명 거래처 조회 실패', err);
    }
  };

  const handlePartnerRegisterOpen = () => {
    const params = new URLSearchParams({
      returnTo: 'projectForm',
      returnToken: partnerReturnTokenRef.current,
    });
    window.open(`/partnermng/add?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    return subscribePartnerCreated((event) => {
      if (
        event.returnTo !== 'projectForm' ||
        event.returnToken !== partnerReturnTokenRef.current ||
        !event.partner?.partnerUuid
      ) {
        return;
      }

      selectedPartner(event.partner);
    });
  }, [selectedPartner]);

  // 담당자 목록 조회
  const fetchPartnerContacts = async (partnerUuid) => {
    if (!partnerUuid) {
      setContactList([]);
      return;
    }
    setLoadingContacts(true);
    try {
      const res = await partnerApi.getPartnerEmp(partnerUuid);
      setContactList(res.data?.list || []);
    } catch (err) {
      console.error('담당자 조회 실패', err);
      setContactList([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  // 담당자 선택 시 정보 자동 채움
  const handleContactSelect = (event, contact) => {
    setSelectedContact(contact);
    if (contact) {
      formik.setFieldValue('chargeNm', contact.employeeNm || '');
      formik.setFieldValue('chargeEml', contact.emailAddr || '');
      formik.setFieldValue('chargeTel', contact.telNo || '');
      formik.setFieldValue('vendorEmp', contact.employeeUuid || '');
    } else {
      formik.setFieldValue('chargeNm', '');
      formik.setFieldValue('chargeEml', '');
      formik.setFieldValue('chargeTel', '');
      formik.setFieldValue('vendorEmp', '');
    }
  };

  // 고객사가 정해지면 담당자 목록 조회
  useEffect(() => {
    if (selectedCustomerUuid) {
      fetchPartnerContacts(selectedCustomerUuid);
    } else {
      setContactList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerUuid]);

  // 필수 필드를 나타내는 마크업
  const requiredMark = (
    <Typography
      variant="subtitle2"
      component="span"
      sx={{ ml: 0.5, color: 'error.main' }}
    >
      *
    </Typography>
  );

  return (
    <div>
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <DetailTemplate title="기회 확인">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              my: 2,
            }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={formik.isSubmitting}
              sx={{ minWidth: '80px' }}
            >
              저장
            </Button>
          </Box>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>프로젝트 기본 정보</Typography>
            
            {/* 첫 번째 행: 프로젝트 타입, 프로젝트 중요도 */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <FormLabel>프로젝트 타입{requiredMark}</FormLabel>
                <BaseCodeSelectbox
                  formik={formik}
                  filterObject={{
                    blcCd: 'BASE',
                    mlcCd: 'FORCAST',
                    slcCd: 'PRJ_TYP',
                    useYn: 'Y',
                  }}
                  name="prjTyp"
                  size="small"
                  required={true}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormLabel>프로젝트 중요도{requiredMark}</FormLabel>
                <BaseCodeSelectbox
                  formik={formik}
                  filterObject={{
                    blcCd: 'BASE',
                    mlcCd: 'FORCAST',
                    slcCd: 'PRJ_IMPT',
                    useYn: 'Y',
                  }}
                  name="prjImpt"
                  size="small"
                  required={true}
                  fullWidth
                />
              </Grid>
            </Grid>

            {/* 두 번째 행: 예산여부, 기회 구분 */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <FormLabel>예산여부{requiredMark}</FormLabel>
                <BaseCodeSelectbox
                  formik={formik}
                  filterObject={{
                    blcCd: 'BASE',
                    mlcCd: 'FORCAST',
                    slcCd: 'BUGET_YN',
                    useYn: 'Y',
                  }}
                  name="bugetYn"
                  size="small"
                  required={true}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormLabel>기회 구분{requiredMark}</FormLabel>
                <BaseCodeSelectbox
                  formik={formik}
                  filterObject={{
                    blcCd: 'BASE',
                    mlcCd: 'FORCAST',
                    slcCd: 'CHANCE_TYP',
                    useYn: 'Y',
                  }}
                  name="chanceTyp"
                  size="small"
                  required={true}
                  fullWidth
                />
              </Grid>
            </Grid>

            {/* 세 번째 행: 거래처 정보 */}
            {/* 거래처 유형(incTyp) 필드 제거 - 0425 p22(4/27)/0515 p33 시안, DB 컬럼은 유지 */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <FormLabel>고객명{requiredMark}</FormLabel>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    required={true}
                    variant="outlined"
                    fullWidth
                    id="partnerInc"
                    name="partnerInc"
                    value={formik.values.partnerInc}
                    onChange={handlePartnerIncChange}
                    onBlur={handlePartnerIncBlur}
                    error={
                      formik.touched.partnerInc &&
                      Boolean(formik.errors.partnerInc)
                    }
                    helperText={
                      formik.touched.partnerInc && formik.errors.partnerInc
                    }
                  />
                  <Button
                    color="secondary"
                    variant="outlined"
                    size="small"
                    onClick={handleOpen}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    고객 선택
                  </Button>
                  <Button
                    color="warning"
                    variant="outlined"
                    size="small"
                    onClick={handlePartnerRegisterOpen}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    거래처 등록
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* 네 번째 행: 사업명, 아이템 구분 */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <FormLabel>사업명{requiredMark}</FormLabel>
                <TextField
                  size="small"
                  required={true}
                  variant="outlined"
                  fullWidth
                  id="cstNm"
                  name="cstNm"
                  value={formik.values.cstNm}
                  onChange={formik.handleChange}
                  error={formik.touched.cstNm && Boolean(formik.errors.cstNm)}
                  helperText={formik.touched.cstNm && formik.errors.cstNm}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormLabel>아이템 구분</FormLabel>
                <BaseCodeSelectbox
                  formik={formik}
                  filterObject={{
                    blcCd: 'BASE',
                    mlcCd: 'FORCAST',
                    slcCd: 'SOL_TYPE',
                    useYn: 'Y',
                  }}
                  name="solutionTyp"
                  size="small"
                  required={false}
                  fullWidth
                />
              </Grid>
            </Grid>

            
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>고객 담당자 정보</Typography>

            {/* 담당자 선택 Autocomplete - 고객사의 등록된 담당자 중에서 고르면 아래 필드가 자동으로 채워진다 */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <FormLabel>담당자 선택</FormLabel>
                <Autocomplete
                  size="small"
                  options={contactList}
                  getOptionLabel={(option) =>
                    option ? `${option.employeeNm || ''}${option.dutiesNm ? ` (${option.dutiesNm})` : ''}` : ''
                  }
                  value={selectedContact}
                  onChange={handleContactSelect}
                  loading={loadingContacts}
                  disabled={!selectedCustomerUuid}
                  noOptionsText={selectedCustomerUuid ? '등록된 담당자가 없습니다' : '고객 선택 버튼으로 거래처를 먼저 선택하세요'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={selectedCustomerUuid ? '담당자를 선택하세요' : '고객 선택 버튼으로 거래처를 먼저 선택하세요'}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingContacts ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option?.employeeUuid === value?.employeeUuid}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  담당자를 선택하면 아래 정보가 자동으로 입력됩니다. 직접 입력도 가능합니다.
                </Typography>
              </Grid>
            </Grid>

            {/* 고객 담당자 정보 */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormLabel>고객 담당자{requiredMark}</FormLabel>
                <TextField
                  size="small"
                  required={true}
                  variant="outlined"
                  fullWidth
                  id="chargeNm"
                  name="chargeNm"
                  value={formik.values.chargeNm}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.chargeNm && Boolean(formik.errors.chargeNm)
                  }
                  helperText={formik.touched.chargeNm && formik.errors.chargeNm}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormLabel>이메일</FormLabel>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  id="chargeEml"
                  name="chargeEml"
                  value={formik.values.chargeEml}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.chargeEml && Boolean(formik.errors.chargeEml)
                  }
                  helperText={
                    formik.touched.chargeEml && formik.errors.chargeEml
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormLabel>연락처</FormLabel>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  id="chargeTel"
                  name="chargeTel"
                  value={formik.values.chargeTel}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.chargeTel && Boolean(formik.errors.chargeTel)
                  }
                  helperText={
                    formik.touched.chargeTel && formik.errors.chargeTel
                  }
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>추가 정보</Typography>
            
            {/* 참고사항 */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel>참고사항</FormLabel>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  id="forcastTxt"
                  name="forcastTxt"
                  multiline
                  rows={3}
                  value={formik.values.forcastTxt}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.forcastTxt &&
                    Boolean(formik.errors.forcastTxt)
                  }
                  helperText={
                    formik.touched.forcastTxt && formik.errors.forcastTxt
                  }
                />
              </Grid>
            </Grid>
          </Paper>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mt: 3,
              gap: 2
            }}
          >
            <Button
              type="button"
              variant="outlined"
              disabled={formik.isSubmitting}
              sx={{ minWidth: '80px' }}
              onClick={() => navigate(-1)}
            >
              취소
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={formik.isSubmitting}
              sx={{ minWidth: '80px' }}
            >
              저장
            </Button>
          </Box>
        </DetailTemplate>
      </Box>
      {open && (
        <PartnerModalConatiner
          link
          title="고객명 선택"
          open={open}
          handleClose={handleClose}
          selectedPartner={selectedPartner}
        />
      )}
    </div>
  );
};

export default ProjectForm;